import React, { useState, useEffect } from "react";

/**
 * Pre-briefing screen - "Urgent message inbound"
 * Creates anticipation before the mission briefing
 */
export function IncomingTransmission({ tour, onAccept }) {
  const [stage, setStage] = useState(0); // 0: fade in, 1: pulse animation, 2: ready to click

  useEffect(() => {
    // Stage 0 -> 1: Start pulse animation
    const timer1 = setTimeout(() => setStage(1), 1000);

    // Stage 1 -> 2: Enable click after 2 seconds
    const timer2 = setTimeout(() => setStage(2), 3000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  return (
    <div
      className="fixed z-50 flex items-center justify-center"
      style={{
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: '#000000',
        animation: 'fadeIn 1s ease-in',
        padding: 'calc(env(safe-area-inset-top, 0px) + 1.5rem) 1.5rem calc(env(safe-area-inset-bottom, 0px) + 1.5rem)'
      }}
      onClick={stage >= 2 ? onAccept : undefined}
    >
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes urgentPulse {
          0%, 100% {
            transform: scale(1);
            opacity: 0.8;
          }
          50% {
            transform: scale(1.05);
            opacity: 1;
          }
        }
        @keyframes signalPulse {
          0% {
            box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
          }
          50% {
            box-shadow: 0 0 0 20px rgba(239, 68, 68, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
          }
        }
        .urgent-pulse {
          animation: urgentPulse 2s ease-in-out infinite;
        }
        .signal-pulse {
          animation: signalPulse 1.5s ease-out infinite;
        }
        @keyframes scanlines {
          0% { background-position: 0 0; }
          100% { background-position: 0 100%; }
        }
        .scanlines {
          background: repeating-linear-gradient(
            0deg,
            rgba(0, 0, 0, 0.1) 0px,
            transparent 1px,
            transparent 2px,
            rgba(0, 0, 0, 0.1) 3px
          );
          background-size: 100% 4px;
          animation: scanlines 8s linear infinite;
        }
      `}</style>

      {/* Scanlines overlay */}
      <div className="scanlines fixed inset-0 pointer-events-none opacity-30" />

      <div className="text-center relative">
        {/* Signal icon with pulse effect */}
        {stage >= 1 && (
          <div className="flex justify-center mb-8">
            <div
              className="relative w-32 h-32 rounded-full flex items-center justify-center signal-pulse"
              style={{
                background: 'rgba(239, 68, 68, 0.2)',
                border: '3px solid #EF4444'
              }}
            >
              {/* Inner pulsing circle */}
              <div
                className="urgent-pulse w-20 h-20 rounded-full flex items-center justify-center"
                style={{
                  background: 'rgba(239, 68, 68, 0.3)',
                  border: '2px solid #EF4444'
                }}
              >
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#EF4444"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="2" fill="#EF4444" />
                  <path d="M16.24 7.76a6 6 0 0 1 0 8.49" />
                  <path d="M7.76 16.24a6 6 0 0 1 0-8.49" />
                  <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                  <path d="M4.93 19.07a10 10 0 0 1 0-14.14" />
                </svg>
              </div>
            </div>
          </div>
        )}

        {/* Urgent message text */}
        {stage >= 1 && (
          <div className="mb-6">
            <div
              className="font-mono text-sm uppercase tracking-widest mb-2"
              style={{ color: '#EF4444' }}
            >
              Priority: <span className="font-bold">URGENT</span>
            </div>
            <h1
              className="font-mono text-4xl md:text-5xl font-bold tracking-wide leading-tight"
              style={{
                color: '#F87171',
                textShadow: '0 0 20px rgba(248, 113, 113, 0.5)'
              }}
            >
              <div>INCOMING</div>
              <div>TRANSMISSION</div>
            </h1>
          </div>
        )}

        {/* Recommendations */}
        {stage >= 1 && (
          <div className="flex flex-col items-center gap-3 mb-8 mx-auto w-fit">
            {/* Header */}
            <div
              className="font-mono text-xs uppercase tracking-widest mb-1"
              style={{ color: '#6B7280' }}
            >
              Tips for Best Experience
            </div>

            {/* Tips container */}
            <div className="flex flex-col gap-3">
              {/* Volume recommendation */}
              <div
                className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg"
                style={{
                  background: 'rgba(16, 185, 129, 0.1)',
                  border: '1px solid rgba(16, 185, 129, 0.3)'
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#10B981"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                  <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                  <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                </svg>
                <span
                  className="font-mono text-xs uppercase tracking-wider"
                  style={{ color: '#10B981' }}
                >
                  Turn Up Volume
                </span>
              </div>

              {/* Low Power Mode recommendation */}
              <div
                className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg"
                style={{
                  background: 'rgba(16, 185, 129, 0.1)',
                  border: '1px solid rgba(16, 185, 129, 0.3)'
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#10B981"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                  <line x1="12" y1="18" x2="12.01" y2="18" />
                  <path d="M12 6v6" />
                  <path d="M8 10l4 4 4-4" />
                </svg>
                <span
                  className="font-mono text-xs uppercase tracking-wider"
                  style={{ color: '#10B981' }}
                >
                  Disable Low Power Mode
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Click to decrypt button */}
        {stage >= 2 && (
          <button
            onClick={onAccept}
            className="group px-8 py-4 rounded-lg font-semibold text-lg uppercase tracking-wide transition-all"
            style={{
              background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
              color: '#ffffff',
              border: '2px solid #EF4444',
              boxShadow: '0 0 30px rgba(239, 68, 68, 0.5)',
              cursor: 'pointer'
            }}
          >
            <span className="flex items-center gap-3">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <span>Click to Open</span>
            </span>
          </button>
        )}

        {/* Initial message - shows before ready to click */}
        {stage < 2 && (
          <div
            className="font-mono text-sm"
            style={{ color: '#6B7280' }}
          >
            Establishing secure connection...
          </div>
        )}
      </div>
    </div>
  );
}
